/**
 * "Formler för uppgiften" (pedagogy spec §4.3.6, B7): a disclosure at the bottom
 * of the work column, not a modal. Opened, it lists the formula cards whose topic
 * is the problem's own or one of its secondary topics, a link to the whole
 * reference, and what the exam allows ("På tentan har du Physics Handbook, …"),
 * since there is no official formula sheet. Collapsed by default, and shut while a
 * plan step waits for its pick: a formula would give the principle away. The list
 * comes from the one cached `GET /api/content/formulas` (`useFormulas`, requested the first time
 * the panel is opened), filtered here by topic.
 */
import clsx from "clsx";
import { ChevronDown, ExternalLink, Sigma } from "lucide-react";
import { useId, useState } from "react";
import { useFormulas, type FormulaDto } from "@client/api/formulas";
import { FormulaCard } from "@client/components/FormulaCard";
import { Spinner } from "@client/components/ui";
import { useT } from "@client/i18n";
import { messageForError } from "@client/lib/errors";

export interface FormulaPanelProps {
  /** The problem's primary topic id. */
  topic: string;
  /** The problem's secondary topic ids (`PublicQuestion.secondaryTopics`). */
  secondaryTopics?: readonly string[];
  /** A plan step waits for its pick: the panel stays shut (pedagogy spec §4.3.6). */
  locked?: boolean;
}

/**
 * The formulas of `topics`, the problem's own topic first and then each
 * secondary topic in the order the problem lists them; within a topic the bank's order.
 */
export function formulasForTopics(
  formulas: readonly FormulaDto[],
  topics: readonly string[],
): FormulaDto[] {
  const order = [...new Set(topics)];
  return order.flatMap((topicId) => formulas.filter((formula) => formula.topic === topicId));
}

export function FormulaPanel({ topic, secondaryTopics = [], locked = false }: FormulaPanelProps) {
  const t = useT();
  const titleId = useId();
  const panelId = useId();
  const lockedNoteId = useId();
  const [open, setOpen] = useState(false);
  // The list is fetched when the panel is first opened, not with every problem (a phone on a
  // slow connection asks for it only when the student wants it); the cache keeps it after that.
  const formulasQuery = useFormulas(undefined, { enabled: open });
  const expanded = open && !locked;
  const matching = formulasForTopics(formulasQuery.data?.formulas ?? [], [
    topic,
    ...secondaryTopics,
  ]);

  return (
    <section aria-labelledby={titleId} className="surface mt-4 p-4">
      <h3 id={titleId} className="text-sm font-semibold text-ink-900 dark:text-ink-50">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-describedby={locked ? lockedNoteId : undefined}
          disabled={locked}
          onClick={() => setOpen((value) => !value)}
          className="flex min-h-8 w-full items-center gap-2 rounded-lg text-left disabled:cursor-not-allowed disabled:opacity-60 max-sm:min-h-11"
        >
          <Sigma size={16} aria-hidden="true" className="text-accent-700 dark:text-accent-300" />
          {t("practice.formulas.toggle")}
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={clsx("ml-auto shrink-0 transition-transform", expanded && "rotate-180")}
          />
        </button>
      </h3>

      {locked && (
        <p id={lockedNoteId} className="mt-2 text-xs font-medium text-ink-700 dark:text-ink-300">
          {t("practice.formulas.afterPlan")}
        </p>
      )}

      <div id={panelId} hidden={!expanded} className="mt-3">
        {expanded && (
          <>
            {formulasQuery.isLoading && (
              <div className="flex justify-center py-4">
                <Spinner label={t("common.loading")} />
              </div>
            )}
            {formulasQuery.isError && (
              <p role="alert" className="text-sm text-ink-700 dark:text-ink-300">
                {messageForError(formulasQuery.error, t)}
              </p>
            )}
            {formulasQuery.isSuccess && matching.length === 0 && (
              <p className="text-sm text-ink-600 dark:text-ink-400">
                {t("practice.formulas.empty")}
              </p>
            )}
            {matching.map((formula) => (
              <FormulaCard key={formula.id} formula={formula} className="mb-3" />
            ))}
            <p className="text-sm">
              {/* A new tab: leaving this page would drop the answer being typed. */}
              <a
                href="/library/formulas"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-accent-800 underline underline-offset-2 hover:text-accent-900 dark:text-accent-200 dark:hover:text-accent-100"
              >
                {t("practice.formulas.all")}
                <ExternalLink size={14} aria-hidden="true" />
                <span className="sr-only">{t("practice.formulas.newTab")}</span>
              </a>
            </p>
            <p className="mt-2 text-xs text-ink-600 dark:text-ink-400">
              {t("practice.formulas.examNote")}
            </p>
          </>
        )}
      </div>
    </section>
  );
}
